﻿import sortArray from 'sort-array';

/**
 * Function that sets up and maintains the state for the AlpineJS powered component.
 * 
 * initialSort: An object passed in with a default FieldName as the key and direction.
 * 
 * allowedSearchKeys: An array of FieldName strings passed in to set the enabled FieldNames that can be searched
 * against with the search input.
 * 
 * enablePagination: If pagination is disabled, then we'll show all rows at all times. Otherwise pagination includes
 * Prev/Next buttons, PageLength select element, and a string to show how many records are being shown.
 * 
 * TODO:
 * This state effectively includes multiple copies of the provided dataset:
 * -- the pagination state that splits the resulting rows into pages (array of arrays)
 * -- a computed state for the current page of results
 * Ideally, this should be revisited to see if this can be cut down to reduce memory footprint. Though perhaps with
 * JS proxy objects, this is a non-issue. Should maybe be investigated!
 * 
 * 
 * Returns an object for Alpine.
 * 
 * @param {array} columns
 * @param {array} initialRows
 * @param {object} initialSort
 * @param {string} initialSort.key
 * @param {string} initialSort.dir
 * @param {array} allowedSearchKeys
 * @param {boolean} enablePagination
 * 
 * @returns {object}
 */
export const textGridState = function ({
	initialSort,
	allowedSearchKeys = [],
	enablePagination = true
}) {
	return {
		/**
		 * PROP Optional list of column FieldNames that can be used for searching
		 * 
		 * @type {array}
		 */
		allowedSearchKeys,

		/**
		 * PROP Option for enabling pagination features. Defaults to true
		 * 
		 * @type {boolean}
		 */
		enablePagination,

		/**
		 * STATE. Entire TextGridVm dataset.
		 * 
		 * @type {object}
		 */
		textgrid: null,

		/**
		 * STATE for the current page length. Defaults to 5
		 * 
		 * @type {number}
		 */
		pageLength: 5,

		/*
		 * STATE Initial page length. Reason to keep this in state, perhaps if a custom page length is passes (e.g. 22)
		 * by keeping this value, we could still show that custom value in the dropdown of page length options.
		 * 
		 * @type {number}
		 */
		initialPageLength: 10,

		/**
		 * STATE for the currently viewed page. This is an array index, starting with 0.
		 * This value is also reset whenever the sorting, page length, column filtering, or search query
		 * is set, sending the user back to page 1.
		 * 
		 * @type {number}
		 */
		currentPageIndex: 0,

		/**
		 * STATE for search query string
		 * 
		 * @type {string}
		 */
		searchQuery: null,

		/**
		 * STATE for the selected column filter options. This is somewhat complicated in that this
		 * is an object, with the key of the object being the FieldName to use for filtering, and the
		 * value is then used in the function for filtering the rows.
		 * 
		 * @type {object}
		 */
		columnFilters: {},

		/**
		 * STATE for the selected column filter options. This is somewhat complicated in that this
		 * is an object, with the key of the object being the FieldName to use for filtering, and the
		 * value is then used in the function for filtering the rows.
		 * 
		 * @type {object}
		 */
		visibleColumns: [{Column: '', Visible: true}],

		/**
		 * STATE for the current sorting and sorting direction setting.
		 * 
		 * @type {object}
		 */
		sortState: { ...initialSort },

		get initialRows() {
			return this.textgrid && this.textgrid.Rows ? this.textgrid.Rows : [];
		},

		/**
			 * COMPUTED. This is the getter responsible for returning the fully filtered and sorted rows. This is also
			 * done in multiple stages, filtering down by columns first, then by search query, then lastly sorting the
			 * dataset. The order here needs to be considered, to successively narrow down the size of the dataset for
			 * performance, especially down to the sorting, smaller the dataset the better.
			 * 
			 * @returns {array}
			 */
		get filteredAndSortedRows() {
			//const filteredByColumn = this.filterByColumnOption(this.initialRows, this.columnFilters);

			//const filteredBySearch = this.filterRows(filteredByColumn, this.allowedSearchKeys, this.searchQuery);


			const textGridRows = this.textgrid && this.textgrid.Rows ? this.textgrid.Rows : [];

			const filteredByFunction = this.filterByFunction(textGridRows);

			const filteredByColumn = this.filterByColumnOption(filteredByFunction, this.columnFilters);

			const filteredBySearch = this.filterRows(filteredByColumn, this.allowedSearchKeys, this.searchQuery);

			return this.sortRows(filteredBySearch);
		},

		/**
		 * INIT METHOD.
		 * Sets the initialPageLength, defaults to 10.
		 * Sets the column filter options.
		 */
		setupComponent: function ({ pageLength = 10, jsonTextGrid }) {
			this.initialPageLength = pageLength;
			this.pageLength = pageLength;

			this.textgrid = Object.freeze(JSON.parse(jsonTextGrid));

			this.setFilteredAndSortedRows();
			this.setColumnFilters();
		},

		/** COMPUTED FUNCTIONS */

		/**
		 * COMPUTED. Get columns from data
		 */
		get columns() {
			return this.textgrid && this.textgrid.ColumnItems ? this.textgrid.ColumnItems.filter(c => !c.ExportOnly) : [];
		},

		/**
		 * COMPUTED. Checks if there are any search keys enabled.
		 * 
		 * @type {boolean}
		 */
		get hasSearchKeys() {
			return this.allowedSearchKeys && this.allowedSearchKeys.length > 0;
		},

		/**
		 * COMPUTED. Sets the page length options. Begins with a default set of options, an option equal to the length
		 * of the dataset (aka All), and the initialPageLength which could be a customized value (e.g. 22). Then this
		 * list is sorted smallest to largest, and filter out any options larger than the dataset. Finally, there is an
		 * edge case there there could be a duplicate value in the array (e.g. a dataset length of 100), this uses a
		 * destructured Set that only returns unique values.
		 * 
		 * @returns {array}
		 */
		get pageLengthOptions() {
			const options = [5, 10, 25, 50, 75, 100, 150, 200, this.initialPageLength, this.filteredAndSortedRows.length]
				.sort((a, b) => a - b)
				.filter(def => def <= this.filteredAndSortedRows.length);

			return [...new Set(options)];
		},

		/**
		 * COMPUTED. Takes the filteredAndSortedRows and splits the dataset into multiple pages. This returns
		 * and array of arrays if pagination is enabled. If pagination is not enabled, skip this and return
		 * a single page of rows.
		 * 
		 * @returns {array}
		 */
		get paginatedRows() {
			if (this.enablePagination) {
				return [...Array(this.pageCount).keys()]
					.map((page) => {
						const { start, end } = this.calcPageStartEnd(page, this.pageLength);

						return this.filteredAndSortedRows.slice(start, end);
					});
			}

			return [this.filteredAndSortedRows];
		},

		/**
		 * COMPUTED. Uses the currentPageIndex state to pick the dataset page to render in the UI.
		 * 
		 * @returns {array}
		 */
		get currentPage() {
			return this.paginatedRows[this.currentPageIndex] || [];
		},

		/**
		 * COMPUTED. Determine if there is a previous page available.
		 * 
		 * @returns {boolean}
		 */
		get hasPreviousPage() {
			return this.currentPageIndex > 0;
		},

		/**
		 * COMPUTED. Determines if there is a next page available.
		 * 
		 * @returns {boolean}
		 */
		get hasNextPage() {
			return this.currentPageIndex + 1 < this.pageCount;
		},

		/**
		 * COMPUTED. Determine the page count, calculated using filtered dataset and page length state.
		 * 
		 * @returns {number}
		 */
		get pageCount() {
			return Math.ceil(this.filteredAndSortedRows.length / this.pageLength);
		},

		/**
		 * COMPUTED. Based on a few state values, render a string for showing the displayed count, and total entries.
		 * 
		 * @returns {string}
		 */
		get displayPageCount() {
			if (this.currentPage && this.currentPage.length > 0) {
				const { start, end } = this.calcPageStartEnd(this.currentPageIndex, this.pageLength);

				return this.filteredAndSortedRows.length > this.pageLength
					? `Showing ${start + 1} to ${end} of ${this.filteredAndSortedRows.length} Entries`
					: `Showing All ${this.filteredAndSortedRows.length} Entries`;
			}

			return '';
		},

		/** METHODS */

		/**
		 * METHOD. This is the getter responsible for returning the fully filtered and sorted rows. This is also
		 * done in multiple stages, filtering down by a specialized filterByFunction, then by columns, then by
		 * search query, then lastly sorting the dataset. The order here needs to be considered, to successively
		 * narrow down the size of the dataset for performance, especially down to the sorting, smaller the dataset the better.
		 * 
		 * @returns {array}
		 */
		setFilteredAndSortedRows: function () {
			const textGridRows = this.textgrid && this.textgrid.Rows ? this.textgrid.Rows : [];

			const filteredByFunction = this.filterByFunction(textGridRows);

			const filteredByColumn = this.filterByColumnOption(filteredByFunction, this.columnFilters);

			const filteredBySearch = this.filterRows(filteredByColumn, this.allowedSearchKeys, this.searchQuery);

			this.filteredAndSortedRows = this.sortRows(filteredBySearch);

		},


		/**
		 * METHOD. Use a function called `textGridFilter` to filter the rows. This function is defined
		 * by a parent wrapping component. The specific name is required!! The rows are then passed to this function
		 * for the necessary and specific advanced filtering
		 * 
		 * @param {Array} rows
		 * @returns {Array}
		 */
		filterByFunction: function (rows) {
			if (typeof this.textGridFilter === 'function') {
				return this.textGridFilter(rows);
			}

			return rows;
		},

		/**
		 * METHOD. Computes the html markup for the entry. Used to better support links and links with icons
		 *
		 * @param {object} rowItem
		 * @returns {string}
		 */
		getDisplayItemMarkup: function (rowItem) {
			if (rowItem.Link === null) {
				return rowItem.DisplayValue;
			}

			if (rowItem.Link.Icon !== null) {
				return `<a href="${rowItem.Link.Href}" target="${rowItem.Link.Target}"><i class="material-symbols-outlined">${rowItem.Link.Icon}</i></a>`;
			}

			return `<a href="${rowItem.Link.Href}" target="${rowItem.Link.Target}">${rowItem.Link.Text}</a>`;
		},

		/**
		 * METHOD. Computes the correct icon sort indicator to use. Return is a string corresponding
		 * to a Material Symbol
		 * 
		 * @param {string} colCheckFieldName
		 * @returns {string}
		 */
		getSortIndicator: function (colCheckFieldName) {
			if (colCheckFieldName === this.sortState.key && this.sortState.dir === 'asc') {
				return 'expand_less';
			}

			if (colCheckFieldName === this.sortState.key && this.sortState.dir === 'desc') {
				return 'expand_more';
			}

			return 'unfold_more';
		},



		/**
		 * METHOD. Function for setting the column filters. Takes the columns, filters out any without
		 * FilterOptions defined, sets the default to an empty string (which is the first option of the select element).
		 */
		setColumnFilters: function () {
			const filterState = {};

			this.columns
				.filter(col => col.FilterOptions)
				.forEach(col => filterState[col.FieldName] = '');

			this.columnFilters = filterState;
		},

		/**
		 * METHOD. Sets the sorting state based on the key provided. If this is already the sortKey, toggle the direction.
		 * If this is a new sort key, set the key and default to desc direction. After setting, reset the currentPageIndex
		 * 
		 * @param {string} key 
		 */
		setSort: function (key) {
			if (key === this.sortState.key) {
				this.sortState.dir = this.sortState.dir === 'desc' ? 'asc' : 'desc';
			} else {
				this.sortState.key = key;
				this.sortState.dir = 'desc';
			}

			this.setFilteredAndSortedRows();
			this.resetCurrentPageIndex();
		},

		/**
		 * METHOD. Function for setting the column filter option. Needed instead of x-model so we could reset the currentPageIndex value
		 * 
		 * @param {string} key 
		 * @param {string} value 
		 */
		setColumnFilterOption: function (key, value) {
			this.columnFilters[key] = value;
			this.setFilteredAndSortedRows();
			this.resetCurrentPageIndex();
		},

		//takes in an array of object with 2 properties (Column = String | Visible = bool)
		SetVisibleColumns: function (VisibleColumns) {
			this.visibleColumns = VisibleColumns.ColumnList;
			return true;
		},

		//alot of guarding and such but in reality its just checking if we have a KV of the column name and if so returning the value which will be a bool
		ShowColumn: function (FieldName) {
			if (typeof this.visibleColumns === "undefined") return true;
			if (this.visibleColumns === null) return true;
			var result = this.visibleColumns.find(obj => {
				if (obj.Column === FieldName) {
					return true;
				}
				else {
					return false;
				}
			});
			if (typeof result === "undefined") return true;
			if (result === null) return true;
			return result.Visible;
		},

		/**
		 * METHOD. Function to filter down the dataset using the columnFilters (which is also available in state).
		 * First, we only want to be concerned with the columnFilters that have actually been set.
		 * Next, if we have any set columnFilters, filter down the rows. If there are no filters set, skip to returning the rows.
		 * In the filter, the filter uses all columnFilters to expect to match. For example, if two columns filters are set,
		 * each row much match those values to be shown.
		 * 
		 * TODO: This could be either an Array.every or an Array.some, the later being more inclusive to showing more rows.
		 *  
		 * Returns an array of rows.
		 * 
		 * @param {array} rows 
		 * @param {object} columnFilters 
		 * @returns {array}
		 */
		filterByColumnOption: function (rows, columnFilters) {
			const columnFilterKeys = Object.keys(columnFilters)
				.filter(key => columnFilters[key] !== '');

			if (columnFilterKeys.length > 0) {
				return rows.filter(row => {
					return columnFilterKeys.every(key => row.RowItems[key].DisplayValue === columnFilters[key]);
				});
			}

			return rows;
		},

		/**
		 * METHOD. Function to filter the rows using the searchQuery string. This expects the component to have been
		 * initialized with a set of keys that can be searched against, and a search query. The filter uses Array.some
		 * to enable searching against any of the allowed keys.
		 * 
		 * Returns an array of row results.
		 * 
		 * @param {array} rows 
		 * @param {array} searchKeys 
		 * @param {string} searchQuery 
		 * @returns {array}
		 */
		filterRows: function (rows, searchKeys, searchQuery) {
			if (
				this.hasSearchKeys &&
				searchQuery &&
				searchQuery.length > 1
			) {
				return rows.filter(row => {
					return searchKeys.some(searchKey => row.RowItems[searchKey].DisplayValue.toLowerCase().includes(searchQuery.FsetC()));
				});
			}

			return rows;
		},

		/**
		 * METHOD. Function to sort the dataset. This uses a third-party library to help in sorting efficiently.
		 * 
		 * TODO: If we upgrade our front end build tooling to support module imports, there are some potetntially
		 * faster sorting functions available. Alternatively we could write our own sorting algorithm implementation
		 * to eliminate the dependency altogether.
		 * 
		 * @see {@link https://github.com/75lb/sort-array} for documentation.
		 * @param {array} rows 
		 * @returns {array}
		 */
		sortRows: function (rows) {
			const { key, dir } = this.sortState;

			if (key && dir) {
				return sortArray(rows, {
					by: key,
					order: dir,
					computed: {
						[key]: row => row.RowItems[key].SortValue,
					},
				});
			}

			return rows;
		},

		/**
		 * METHOD. Calculate the page start and end values based on the page number and page length
		 * @param {numer} pageNum 
		 * @param {number} pageLength 
		 * @returns {object}
		 */
		calcPageStartEnd: function (pageNum, pageLength) {
			const start = (pageNum * pageLength);
			const end = (start + pageLength);

			return { start, end };
		},

		/**
		 * METHOD. Reset currentPageIndex
		 */
		resetCurrentPageIndex: function () {
			this.currentPageIndex = 0;
		},
	};
};

/**
 * Text Grid Export state
 */
export const textGridExport = function () {
	return {
		/**
		 * STATE. Error message.
		 * 
		 * @type {string}
		 */
		error: null,

		/**
		 * STATE. State of the export button.
		 * 
		 * @type {string}
		 */
		buttonState: 'initial',

		/**
		 * STATE. blob download URL.
		 *
		 * @type {string}
		 */
		downloadUrl: null,

		/**
		 * METHOD. Starts the export process.
		 * 1. Get the datasets from the provided list of element ids. Check they have data.
		 * 2. If no datasets could be found, error.
		 * 3. Generate the excel file from the Async endpoint
		 * 
		 * @param {Object} args
		 * @param {Array} args.datasourceIds
		 */
		startExport({ datasourceIds }) {
			this.error = null;
			this.buttonState = 'loading';

			const textgrids = datasourceIds
				.map(id => this.getDatasetFromElement(id))
				.filter(d => Object.keys(d).length > 0);

			if (textgrids.length === 0) {
				this.error = 'No datasets found';
				this.buttonState = 'error';
				return;
			}

			this.generateExcel(textgrids);
		},

		/**
		 * METHOD. Starts the export process using directly provided data
		 * 
		 * @param {Object} args
		 * @param {Array} args.providedData
		 */
		startExportWithProvidedData({ providedData }) {
			this.error = null;
			this.buttonState = 'loading';

			const textgrids = Object.freeze(providedData);

			if (textgrids.length === 0) {
				this.error = 'No datasets found';
				this.buttonState = 'error';

				return;
			}

			this.generateExcel(textgrids);
		},

		/**
		 * METHOD. Takes in an element id and queries the DOM for it, checks that it is found, 
		 * has a dataset, and a textgridvm dataset.
		 * 
		 * @param {string} elementId
		 */
		getDatasetFromElement(elementId) {
			const datael = document.getElementById(elementId);

			return datael && datael.dataset && datael.dataset.textgridvm
				? Object.freeze(JSON.parse(datael.dataset.textgridvm))
				: [];
		},

		/**
		 * METHOD. Makes the async request to get the excel file.
		 * 1. We need an Antiforgery Token from the header to include in request. If
		 * there is no token, error state.
		 * 2. Setup the request
		 * 3. Make the request using native browser fetch API
		 * 4. On error, set messages
		 * 5. On success, get the response blob, generate as objectURL, set the URL
		 * and use JS to trigger a click event on the element to start the download.
		 * 
		 * @param {any} textgrids
		 */
		async generateExcel(textgrids) {
			const afToken = document.head.querySelector('meta[name="af-token"]')
				? document.head.querySelector('meta[name="af-token"]').content
				: null;

			if (afToken === null) {
				this.error = 'No Request Token Available';
				this.buttonState = 'error';
				return;
			}

			const request = new Request('/Async/TextGridToXlsx', {
				method: 'POST',
				credentials: 'same-origin',
				headers: new Headers([
					['Content-Type', 'application/json'],
					['RequestVerificationToken', afToken],
				]),
				body: JSON.stringify(textgrids),
			});

			try {
				const response = await fetch(request);

				if (response.ok) {
					const fileBlob = await response.blob();
					const fileObjUrl = URL.createObjectURL(fileBlob);
					this.buttonState = 'success';
					this.downloadUrl = fileObjUrl;

					this.$nextTick(() => this.$refs.exportDownloadLink.click());
				} else {
					this.error = ' Unable to Generate';
					this.buttonState = 'error';
				}
			} catch (e) {
				console.error(e);
				this.error = 'Unable to Generate';
				this.buttonState = 'error';
			}
		},
	};
};

export const etfSearchFiltering = {
	/**
		* STATE. Holds the state for the active filters. There is a concept between AND filters and OR filters.
		* Primarily, this is the difference between parent filters or filters with no children, and then the filters
		* that are children themselves. We and the expect all the parent filters to be present (reductive), but
		* within the children we want to be additive.
		*
		* @type {object}
		*/
	activeFilters: {
		ands: [],
		ors: [],
	},

	/**
		* STATE. This is an array of arrays, where each array represents the tagIds for each resulting fund match. We
		* do this to avoid needing to store a whole copy or reference to the full row data.
		* 
		* This version is the fullly applied filters, including the `ors` and text search
		*
		* @type {array}
		*/
	filteredRowsTagIds: [],

	/**
		* STATE. Similar to above. This version only filters using the text search and the `ands` filters. Useful to
		* improve the count of the filter options within the child filter groups
		*
		* @type {array}
		*/
	filteredRowsTagIdsByAnd: [],

	/**
		* STATE. Search string for text bases search
		* 
		* @type {string}
		*/
	searchString: '',

	/**
		* INIT METHOD.
		* Sets the initialPageLength, defaults to 10.
		* Sets the column filter options.
		*/
	setFilterWatchers: function () {
		this.$watch('activeFilters', () => this.$dispatch('recalculate-rows'));
		this.$watch('searchString', () => this.$dispatch('recalculate-rows'));
	},

	/** COMPUTED FUNCTIONS */

	/**
		* COMPUTED. Count the current filtered results.
		* 
		* @returns {number}
		*/
	get currentResultCount() {
		return this.filteredRowsTagIds.length;
	},

	/** METHODS */


	/**
		* METHOD. Given a context for activeFilters (`ands` or `ors`) determine if the provided tag is active.
		*
		* @returns {number}
		*/
	isTagIdActive: function (context, tagId) {
		return this.activeFilters[context].includes(Number(tagId));
	},

	/**
		* METHOD. Reset all active filters and search string
		*/
	resetFilters() {
		this.searchString = '';
		this.activeFilters.ands = [];
		this.activeFilters.ors = [];
		this.$nextTick(() => { this.$dispatch('recalculate-rows') });
	},

	/**
		* METHODS. Toggle the provided state of the clicked on filter. This is attempting to put the correct values
		* into the activeFilters `ands` and `ors` state, depending on a few situations. More details below in line.
		* 
		* @param {object} params
		* @param {string|number} params.andVal
		* @param {string|number} params.orVal
		* @param {object} params.groupChildren
		*/
	toggleActiveTag: function ({ andVal, orVal = null, groupChildren = null }) {
		const andValNum = andVal ? Number(andVal) : null;
		const orValNum = orVal ? Number(orVal) : null;

		// If Or is null, and groupChildren are present, we can assume this is an 'all' toggle
		if (orValNum === null && groupChildren && groupChildren.length > 0) {
			groupChildren.forEach((child) => {
				if (this.calculateMatchCount(child) == 0) {
				}
				else if (this.activeFilters.ors.includes(child)) {
					this.activeFilters.ors.splice(this.activeFilters.ors.indexOf(child), 1);
				}
				else {
					this.activeFilters.ors.push(child);
				}
			});
		}

		// If groupChildren are provided, we need to check if any of the children are active filters
		// in the `ors` array, if so we need to add the parent filter to the `ands` array. Else, remove
		// the parent from the `ands` array
		if (groupChildren?.length > 0) {
			this.$nextTick(() => {
				if (this.activeFilters.ors.some(o => groupChildren.includes(o))) {
					this.activeFilters.ands = [...new Set(this.activeFilters.ands.concat([andValNum]))];
				} else {
					this.activeFilters.ands = this.activeFilters.ands.filter(a => a !== andValNum);
				}
			});
		}
		this.$nextTick(() => { this.$dispatch('recalculate-rows') });
	},

	/**
		* METHOD. Given a provided tagId, check the filtered tagIds using only `ands` to calculate the potential match count.
		*
		* @returns {number}
		*/
	calculateMatchCount: function (tagId) {
		var temp = [...new Set(this.filteredRowsTagIdsByAnd.filter(row => row.includes(tagId)))];
		return temp.length;
	},

	/**
		* METHOD. Given a provided tagId, check the filtered tagIds using only `ands` to calculate the potential match count.
		*
		* @returns {number}
		*/
	calculateTotalMatchCount: function (tagIds) {
		var counter = 0;

		for (let i = 0; i < tagIds.length; i++) {
			counter += this.calculateMatchCount(tagIds[i]);
		}

		return counter;
	},

	/**
		* METHOD. Filter the given rows based on the searchString, using the searchKeys
		*
		* @param {array} rows
		* @returns {array}
		*/
	textSearchFilter: function (rows) {
		const searchKeys = [
			'FundName',
			'TickerSymbol',
			'IntradayNavTickerSymbol',
		];

		if (this.searchString?.length > 0 && rows && rows.length > 0) {
			var returnRows = [];
			rows.forEach((row) => {
				searchKeys.forEach((searchKey) => {
					if (row.RowItems[searchKey] &&
						row.RowItems[searchKey].DisplayValue &&
						row.RowItems[searchKey].DisplayValue.toLowerCase().includes(this.searchString.toLowerCase())) {
						if (returnRows.indexOf(row) == -1) {
							returnRows.push(row);
						}
					}
				});
			});
			return returnRows;
		}
		else {
			return rows;
		}
	},

	/**
	* METHOD. Does the advanced filtering of the rows. Details below.
	* 
	* @param {any} rows
	*/
	textGridFilter: function (rows) {
		let finalFilteredRows = rows;

		// Check if there are any active `ands` filters. There should always be at least one
		// `ands` filter, even of only `ors` filters are selected as there should be a parent filter
		if (this.activeFilters.ands.length > 0) {
			finalFilteredRows = rows.filter(row => {
				// Check that every active `ands` filter is present in the row's FilterTagIds. Array.every() returns a boolean
				const andsCheck = this.activeFilters.ands.every(a => row.FilterTagIds.indexOf(Number(a)) !== -1);

				// Check that any active `ors` filter is present in the row's FilterTagIds. Array.some() returns a boolean
				let orsCheck = true;
				if (this.activeFilters.ors.length > 0) {
					orsCheck = this.activeFilters.ors.some(o => row.FilterTagIds.indexOf(Number(o)) !== -1);
				}

				// We want both the andsCheck and the orsCheck to be true
				return andsCheck && orsCheck;
			});
		}

		// Once the filtering by `ands`/`ors` is done, finally do a text search against the remaining rows
		finalFilteredRows = this.textSearchFilter(finalFilteredRows);

		// Set the internal state for tagIds to use in counting
		this.setFilteredRowsTagIds({ rows, finalFilteredRows });

		// Finally, return the filtered rows
		return finalFilteredRows;
	},

	/**
	* METHOD. Set the filteredRowsTagIds and filteredRowsTagIdsByAnd state that is used for dynamic counting of the
	* filter results alongside each filter option. filteredRowsTagIdsByAnd uses only the active `ands` and then also the text search.
	* 
	* @param {object} params
	* @param {array} params.rows
	* @param {array} params.finalFilteredRows
	*/
	setFilteredRowsTagIds({ rows, finalFilteredRows }) {
		this.filteredRowsTagIds = finalFilteredRows.map(row => row.FilterTagIds);

		const filterByAnds = rows.filter(row => {
			let andsCheck = true;

			if (this.activeFilters.ands.length > 0) {
				andsCheck = this.activeFilters.ands.every(a => row.FilterTagIds.indexOf(Number(a)) !== -1);
			}

			return andsCheck;
		});

		this.filteredRowsTagIdsByAnd = this.textSearchFilter(filterByAnds).map(r => r.FilterTagIds);
	},

	//receives a column list and dispatches it back to the text grid so it can use it as a master list for displaying thru alpine
	//columnList will be a serialized version of the FtUiColumnFilters attribute of the FtUiTab
	SetVisibleColumns: function (columnList) {
		var ColumnList = JSON.parse(columnList);
		this.$nextTick(() => { this.$dispatch('set-visible-columns', { ColumnList: ColumnList }) });
		return true;
	},
};
